/**
 * 売上実績データの型定義
 */
export interface SalesResult {
    id: number;
    construction_id: number;
    status: SalesStatus;
    billing_month: string;  // YYYY-MM-DD形式
    sales_month: string;    // YYYY-MM-DD形式
    sales_amount: string | number;   // 売上金額
    labor_cost: string | number;     // 人件費
    vehicle_cost: string | number;   // 車両費
    expense_cost: string | number;   // 諸経費
    vendor_cost: string | number;    // 外注費
    other_cost: string | number;     // その他費用
    gross_profit: string | number;   // 粗利
    gross_profit_rate: string | number; // 粗利率
    base_profit: string | number;   // 基準利益
    supervisor_allowance_total: string | number; // 職長手当合計
    supervisor_allowance_rate: string | number; // 基準利益率
    supervisor_total_days: string | number; // 作業日合計
    supervisor_non_manager_days: string | number; // 担当以外の職長作業日数
    final_profit: string | number; // 最終利益
    final_profit_rate: string | number; // 最終利益率
    ready_at: string | null;         // Ready状態になった日時
    ready_by: number | null;         // Ready状態にした事務員ID
    confirmed_at: string | null;     // Confirmed状態になった日時
    confirmed_by: number | null;     // Confirmed状態にした事務員ID
    created_at: string;              // 作成日時
    updated_at: string;              // 更新日時
    
    // API応答に含まれる追加フィールド
    Construction_Details?: string;   // 工事詳細
    Customer_Name?: string;          // 顧客名
    Member_Name?: string;            // メンバー名
    Site_Name?: string;              // 現場名
    
    // 新しい表示要件に必要なフィールド
    invoice_index: string | number;  // 当月請求No
    completion_date: string;         // 作業完了日
}

/**
 * 売上状態の定義
 */
export enum SalesStatus {
    DRAFT = 'Draft',      // 下書き
    READY = 'Ready',      // 準備完了 
    CONFIRMED = 'Confirmed' // 確定済み
}

/**
 * 売上一覧取得レスポンス
 */
export interface SalesListResponse {
    success: boolean;
    salesResults: SalesResult[];     // APIから返却される売上一覧データ
    total: number;                   // 合計件数
    message?: string;
}

/**
 * 売上集計実行リクエスト
 */
export interface SalesCalculateRequest {
    targetMonth: string;  // YYYY-MM形式
    userLastName: string; // ユーザーの苗字
}

/**
 * 売上集計レスポンス
 */
export interface SalesCalculateResponse {
    success: boolean;
    data: {
        calculatedCount: number;
        salesResults: Array<{
            id: number;
            constructionId: number;
            status: string;
            updated: boolean;
        }>;
    };
    message?: string;
}

/**
 * 売上ステータス更新リクエスト
 */
export interface SalesStatusUpdateRequest {
    status: SalesStatus;
    userLastName: string; // ユーザーの苗字
}

/**
 * 売上検索パラメータ
 */
export interface SalesSearchParams {
    billingMonth?: string;  // YYYY-MM形式
    salesMonth?: string;    // YYYY-MM形式
    status?: SalesStatus;
    customerId?: number;
    constructionId?: number;
}

/**
 * 売上レポート取得リクエスト
 */
export interface SalesReportRequest {
    period: 'monthly' | 'semiannual' | 'annual';
    targetDate: string;  // YYYY-MM形式、半期・年次の場合は開始月
}

/**
 * 売上レポートデータ
 */
export interface SalesReportData {
    period: string;  // 期間表示（例: "2023年4月" or "2023年上半期" or "2023年度"）
    totalSales: number;
    totalProfit: number;
    profitRatio: number;  // 利益率（%）
    monthlySales?: {
        month: string;
        sales: number;
        profit: number;
    }[];
    customerSales?: {
        customerName: string;
        sales: number;
        profit: number;
    }[];
    costBreakdown?: {
        category: string;  // 費用カテゴリ（人件費、車両費など）
        amount: number;
        ratio: number;    // 総費用に対する割合（%）
    }[];
}

/**
 * 売上レポートレスポンス
 */
export interface SalesReportResponse {
    success: boolean;
    data: SalesReportData;
    message?: string;
}

/**
 * 数値を通貨形式（¥1,234,567）にフォーマットする関数
 * 文字列または数値型の入力に対応
 */
export const formatCurrency = (amount: string | number | undefined | null): string => {
  if (amount === undefined || amount === null || amount === '') return '-';
  
  // 文字列の場合は数値に変換
  const numAmount = typeof amount === 'string' ? Number(amount) : amount;
  
  // 無効な数値の場合は「-」を返す
  if (isNaN(numAmount)) return '-';
  
  // 通貨形式（カンマ区切り）で返す
  return `¥${numAmount.toLocaleString()}`;
};

/**
 * 日付をYYYY/MM/DD形式にフォーマットする関数
 * すでに文字列形式の場合はそのまま表示形式に変換
 */
export const formatDate = (dateStr: string | undefined | null): string => {
  if (!dateStr) return '-';
  
  try {
    // YYYY-MM-DD形式の文字列をYYYY/MM/DD形式に変換
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      // すでにYYYY-MM-DD形式の文字列であれば、「/」区切りに変換
      return `${parts[0]}/${parts[1]}/${parts[2]}`;
    }
    
    // その他の形式の場合、デフォルト処理を行う
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '-'; // 無効な日付
    
    return date.toLocaleDateString('ja-JP');
  } catch (e) {
    console.error('日付フォーマットエラー:', e);
    return '-';
  }
};

/**
 * ステータス表示用のマッピング
 */
export const statusDisplayMap: Record<SalesStatus, string> = {
  [SalesStatus.DRAFT]: '下書き',
  [SalesStatus.READY]: '確認待ち',
  [SalesStatus.CONFIRMED]: '確定済'
};

/**
 * ステータスに応じた色を返す関数
 */
export const getStatusColor = (status: SalesStatus): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
  switch (status) {
    case SalesStatus.DRAFT:
      return 'default';
    case SalesStatus.READY:
      return 'primary';
    case SalesStatus.CONFIRMED:
      return 'success';
    default:
      return 'default';
  }
};



/**
 * 職長手当データの型定義
 */
export interface SupervisorAllowance {
    id: number;
    sales_result_id: number;
    construction_id: number;
    billing_month: string;  // YYYY-MM-DD形式
    sales_month: string;    // YYYY-MM-DD形式
    supervisor_name: string;  // 職長名
    supervisor_date: string;  // 作業日
    supervisor_date_str?: string;  // 日付表示用
    is_manager: boolean;  // 担当職長かどうか
    allowance_amount: number;  // 手当金額
    created_at: string;
    
    // JOIN時に取得する追加フィールド
    Customer_Name?: string;  // 顧客名
    Site_Name?: string;  // 現場名
    Construction_Details?: string;  // 工事詳細
    Manager_Name?: string;  // 担当者名
}

/**
 * 職長手当集計データの型定義
 */
export interface SupervisorAllowanceSummary {
    supervisor_name: string;  // 職長名
    project_count: number;  // 工事件数
    total_days: number;  // 総日数
    manager_days: number;  // 担当日数
    non_manager_days: number;  // 非担当日数
    total_allowance: number;  // 手当総額
}

/**
 * 職長手当一覧取得レスポンス
 */
export interface SupervisorAllowanceListResponse {
    success: boolean;
    total: number;
    allowances: SupervisorAllowance[];
    message?: string;
}

/**
 * 職長手当集計取得レスポンス
 */
export interface SupervisorAllowanceSummaryResponse {
    success: boolean;
    total: number;
    summary: SupervisorAllowanceSummary[];
    message?: string;
}

/**
 * 職長手当検索パラメータ
 */
export interface SupervisorAllowanceSearchParams {
    yearMonth: string;  // YYYY-MM形式
    supervisorName?: string;  // 職長名
}

/**
 * 職長手当ステータス状態を文字列で返す関数
 */
export const formatManagerStatus = (isManager: boolean): string => {
    return isManager ? '担当' : '非担当';
};

/**
 * 職長手当一覧表示のヘッダー定義
 */
export const supervisorAllowanceHeaders = [
    { field: 'supervisor_name', headerName: '職長名', width: 120 },
    { field: 'Manager_Name', headerName: '担当者', width: 120 },
    { field: 'Customer_Name', headerName: '会社名', width: 150 },
    { field: 'Construction_Details', headerName: '現場名', width: 200 },
    { 
        field: 'supervisor_date', 
        headerName: '日付', 
        width: 120,
        valueFormatter: (params: any) => formatDate(params.value)
    },
    { 
        field: 'allowance_amount', 
        headerName: '職長手当', 
        width: 120,
        valueFormatter: (params: any) => formatCurrency(params.value),
        align: 'right',
        headerAlign: 'right'
    },
    { 
        field: 'is_manager', 
        headerName: 'マイナス', 
        width: 100,
        valueFormatter: (params: any) => params.value ? '✓' : '-',
        align: 'center',
        headerAlign: 'center'
    }
];

/**
 * 職長手当集計表示のヘッダー定義
 */
export const supervisorAllowanceSummaryHeaders = [
    { field: 'supervisor_name', headerName: '職長名', width: 120 },
    { 
        field: 'project_count', 
        headerName: '工事件数', 
        width: 100,
        align: 'right',
        headerAlign: 'right'
    },
    { 
        field: 'total_days', 
        headerName: '総日数', 
        width: 100,
        align: 'right',
        headerAlign: 'right'
    },
    { 
        field: 'manager_days', 
        headerName: '担当日数', 
        width: 100,
        align: 'right',
        headerAlign: 'right'
    },
    { 
        field: 'non_manager_days', 
        headerName: '非担当日数', 
        width: 100,
        align: 'right',
        headerAlign: 'right'
    },
    { 
        field: 'total_allowance', 
        headerName: '手当総額', 
        width: 120,
        valueFormatter: (params: any) => formatCurrency(params.value),
        align: 'right',
        headerAlign: 'right'
    }
];


/**
 * 集計日取得レスポンス
 */
export interface CalculationDateResponse {
    success: boolean;
    calculationDate: string | null;  // 集計日（最終更新日）
  }

/**
 * 集計前チェック: 未完了の請求依頼
 */
export interface PreCheckIncompleteInvoice {
    ID: number;
    Quotation_No: string;
    Customer_Name: string;
    Site_Name: string;
    Superviser: string;
}

/**
 * 集計前チェックレスポンス
 */
export interface PreCheckResponse {
    success: boolean;
    month: string;
    aggregated: boolean;              // この月の集計が実行済みか
    lastCalculatedAt: string | null;
    incompleteCount: number;          // 未完了の請求依頼数
    incompleteInvoices: PreCheckIncompleteInvoice[];
}

/**
 * 要対応一覧: 保留中(費用未確定)の売上データ
 */
export interface AttentionPendingDraft {
    id: number;
    construction_id: number;
    Quotation_No: string;
    Customer_Name: string;
    Site_Name: string;
    Superviser: string;
    billing_month: string;            // YYYY-MM
    unconfirmed_expenses: number;     // 金額未入力の諸経費明細数
    unconfirmed_vendors: number;      // 金額未入力の業者発注明細数
    pending_days: number;             // 保留日数
}

/**
 * 要対応一覧: 計上漏れ(完了済み請求なのに売上データが無い)
 */
export interface AttentionOrphan {
    invoice_id: number;
    Construction_ID: number;
    Quotation_No: string;
    Customer_Name: string;
    Site_Name: string;
    Superviser: string;
    billing_month: string;            // YYYY-MM
    completed_date: string | null;
}

/**
 * 要対応一覧: 差異あり(計上済みだが保存値と現在値がズレている)の1項目
 */
export interface AttentionMismatchField {
    label: string;    // 項目名（売上額/諸経費/業者発注/人件費/車両費）
    stored: number;   // 計上時の保存値
    current: number;  // 現在のソース値
}

/**
 * 要対応一覧: 差異あり(⑤⑨)
 */
export interface AttentionMismatch {
    id: number;
    construction_id: number;
    Quotation_No: string;
    Customer_Name: string;
    Site_Name: string;
    Superviser: string;
    billing_month: string;            // YYYY-MM
    status: string;                   // Ready / Confirmed
    diffFields: AttentionMismatchField[];
}

/**
 * 要対応一覧: 未集計の月(⑥)
 */
export interface AttentionUncalculatedMonth {
    billing_month: string;            // YYYY-MM
    completed_count: number;          // その月の完了済み請求数
}

/**
 * 要対応一覧レスポンス
 */
export interface AttentionResponse {
    success: boolean;
    pendingDrafts: AttentionPendingDraft[];
    orphans: AttentionOrphan[];
    mismatches: AttentionMismatch[];
    uncalculatedMonths: AttentionUncalculatedMonth[];
    summary: {
        pendingCount: number;
        orphanCount: number;
        mismatchCount: number;
        uncalculatedMonthCount: number;
    };
}
  
  // 売上データ取得サービスのインターフェースにも追加が必要です
  // サービス関数として以下のようなものを追加すると良いでしょう
  
  /**
   * 指定した年月の集計日（最終更新日）を取得する関数
   * @param yearMonth YYYY-MM形式の年月
   * @returns 集計日（最終更新日）または null
   */
  export const getCalculationDate = async (yearMonth: string): Promise<string | null> => {
    try {
      const response = await fetch(`/api/sales/calculation-date/${yearMonth}`);
      const data: CalculationDateResponse = await response.json();
      
      if (data.success) {
        return data.calculationDate;
      }
      return null;
    } catch (error) {
      console.error('集計日取得エラー:', error);
      return null;
    }
  };
  
  /**
   * 日時をYYYY/MM/DD HH:mm形式にフォーマットする関数
   * API応答の集計日表示用
   */
  export const formatDateTime = (dateTimeStr: string | null | undefined): string => {
    if (!dateTimeStr) return '-';
    
    try {
      // YYYY-MM-DD HH:mm:ss形式の文字列をYYYY/MM/DD HH:mm形式に変換
      const date = new Date(dateTimeStr);
      if (isNaN(date.getTime())) return '-'; // 無効な日付
      
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      
      return `${year}/${month}/${day} ${hours}:${minutes}`;
    } catch (e) {
      console.error('日時フォーマットエラー:', e);
      return '-';
    }
  };


  // ========== レポート関連の型定義 ==========


  // 既存のコードに以下を追加

/**
 * レポートタイプの定義
 */
export type ReportType = 'supervisor' | 'supervisor-customer' | 'company-trend';

/**
 * レポート単位の定義
 */
export type ReportUnit = 'monthly' | 'quarterly' | 'half' | 'yearly';

/**
 * レポート期間の型定義
 */
export interface ReportPeriod {
  startMonth: string; // YYYY-MM形式
  endMonth: string; // YYYY-MM形式
}

/**
 * レポートフィルター条件の型定義
 */
export interface ReportFilters {
  period: ReportPeriod;
  supervisor?: string;
  customer?: string;
  unit?: ReportUnit;
}

/**
 * レポートAPIパラメータの型定義
 */
export interface ReportParams {
  startMonth: string;
  endMonth: string;
  unit?: ReportUnit;
}

/**
 * レポートフィルターの状態管理用型定義
 */
export interface ReportFilterState {
  startDate: string;
  endDate: string;
  unit?: ReportUnit;
}

/**
 * 担当者レポートアイテムの型定義
 */
export interface SupervisorReportItem {
  supervisor_name: string; // 担当者名
  project_count: number; // 工事件数
  total_sales: number; // 売上合計
  gross_profit: number; // 粗利益合計
  gross_profit_rate: number; // 粗利益率
  supervisor_allowance: number; // 職長手当合計
  final_profit: number; // 最終利益合計
  final_profit_rate: number; // 最終利益率
}
  
  /**
   * 担当者×顧客レポートアイテムの型定義
   */
  export interface SupervisorCustomerReportItem extends SupervisorReportItem {
    customer_name: string; // 顧客名
  }
  
  /**
   * 会社全体トレンドレポートアイテムの型定義
   */
  export interface CompanyTrendReportItem {
    period: string; // 期間（例: '2023-01', '2023-Q1', '2023'）
    project_count: number; // 完了工事件数
    total_sales: number; // 売上合計
    gross_profit: number; // 粗利益合計
    gross_profit_rate: number; // 粗利益率
    supervisor_allowance: number; // 職長手当合計
    final_profit: number; // 最終利益合計
    final_profit_rate: number; // 最終利益率
    sales_yoy?: number | null; // 前年同期比（売上）
    profit_yoy?: number | null; // 前年同期比（利益）
    previous_sales?: number; // 前年同期売上
    previous_profit?: number; // 前年同期利益
  }
  
  /**
   * フィルター用の型定義
   */
  export interface FilterItem {
    name: string;
    id?: number;
  }
  
  /**
   * 担当者レポートレスポンスの型定義
   */
  export interface SupervisorReportResponse {
    success: boolean;
    period: ReportPeriod;
    data: SupervisorReportItem[];
  }
  
  /**
   * 担当者×顧客レポートレスポンスの型定義
   */
  export interface SupervisorCustomerReportResponse {
    success: boolean;
    period: ReportPeriod;
    filters: {
      supervisor: string | null;
      customer: string | null;
      confirmed: boolean;
    };
    data: SupervisorCustomerReportItem[];
  }
  
  /**
   * 会社全体トレンドレポートレスポンスの型定義
   */
  export interface CompanyTrendReportResponse {
    success: boolean;
    period: ReportPeriod;
    unit: ReportUnit;
    data: CompanyTrendReportItem[];
  }
  
  /**
   * フィルター項目レスポンスの型定義
   */
  export interface FilterListResponse {
    success: boolean;
    data: FilterItem[];
  }
  
  /**
   * レポート用の共通フォーマット関数
   */
  
  /**
   * 数値を利益率表示用にフォーマットする関数
   */
  export const formatProfitRate = (rate: number | string | undefined | null): string => {
    if (rate === undefined || rate === null || rate === '') return '-';
    
    // 文字列の場合は数値に変換
    const numRate = typeof rate === 'string' ? Number(rate) : rate;
    
    // 無効な数値の場合は「-」を返す
    if (isNaN(numRate)) return '-';
    
    // 少数第1位まで表示して%を付ける
    return `${numRate.toFixed(1)}%`;
  };
  
  /**
   * 前年同期比をフォーマットする関数
   */
  export const formatYoYRate = (rate: number | string | undefined | null): string => {
    if (rate === undefined || rate === null || rate === '') return '-';
    
    // 文字列の場合は数値に変換
    const numRate = typeof rate === 'string' ? Number(rate) : rate;
    
    // 無効な数値の場合は「-」を返す
    if (isNaN(numRate)) return '-';
    
    // プラスの場合は+記号を付け、少数第1位まで表示して%を付ける
    return `${numRate > 0 ? '+' : ''}${numRate.toFixed(1)}%`;
  };
  
  /**
   * 期間表示をフォーマットする関数（例: 2023-01 → 2023年1月）
   */
  export const formatPeriodLabel = (period: string): string => {
    if (!period) return '-';
    
    try {
      // YYYY-MM形式の場合
      if (/^\d{4}-\d{2}$/.test(period)) {
        const [year, month] = period.split('-');
        return `${year}年${parseInt(month)}月`;
      }
      
      // YYYY-QN形式の場合（四半期）
      if (/^\d{4}-Q[1-4]$/.test(period)) {
        const [year, quarter] = period.split('-');
        return `${year}年${quarter}`;
      }
      
      // YYYY形式の場合（年次）
      if (/^\d{4}$/.test(period)) {
        return `${period}年`;
      }
      
      return period;
    } catch (e) {
      console.error('期間表示フォーマットエラー:', e);
      return period;
    }
  };